<template>
  <SModal modal-id="file-select" :max-content-width="816">
    <div class="file-select-modal">
      <div class="file-select-modal__title">
        Добавление файла
      </div>
      <div
        class="file-select-modal__drop-area"
        @dragover.prevent
        @drop.prevent="onDrop"
      >
        <div class="file-select-modal__drop-area-title">
          {{ dropAreaTitle }}
        </div>
        <div v-if="description" class="file-select-modal__drop-area-description">
          {{ description }}
        </div>
        <SButton
          variant="primary"
          size="small"
          class="file-select-modal__drop-area-button"
          @click="onButtonClick"
        >
          {{ dropAreaButtonText }}
        </SButton>
      </div>
      <input
        v-show="false"
        ref="baseUploaderInput"
        type="file"
        :accept="accept"
        @input="onInput"
      />
    </div>
  </SModal>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

import { useModal } from '@/components/modal/useModal'
import { UploaderFile } from '@/types/uploader'

import SModal from '@/components/modal/SModal.vue'
import SButton from '@/components/button/Button.vue'

export type Props = {
  accept: string
  description: string
  dropAreaTitle: string
  dropAreaButtonText: string
}

const file = defineModel<UploaderFile | null>('file')

const props = defineProps<Props>()

const emit = defineEmits<{
  'close': []
}>()

const { openModal, close } = useModal()

const baseUploaderInput = ref<HTMLInputElement | null>(null)

function onInput() {
  if (!baseUploaderInput.value) return

  const newFile = baseUploaderInput.value.files?.[0]

  if (newFile) {
    file.value = newFile
    baseUploaderInput.value.value = ''
    close('file-select')
    emit('close')
  }
}

function onDrop(event: DragEvent) {
  if (event.dataTransfer === null) return

  const newFile = event.dataTransfer.files[0]

  if (props.accept.split(',').includes(`.${getFileExtension(newFile)}`)) {
    file.value = newFile
    close('file-select')
    emit('close')
  }
}

function onButtonClick() {
  if (baseUploaderInput.value) {
    baseUploaderInput.value.click()
  }
}

function getFileExtension(file: UploaderFile) {
  return file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2)
}

onMounted(async () => {
  try {
    await openModal('file-select', {})
  } catch (error) {
    if (error === 'closed') {
      emit('close')
    } else {
      console.warn(error)
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins.scss';

.file-select-modal {
  &__title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    margin-bottom: 24px;
  }

  &__drop-area {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 196px;
    padding: 28px;
    border: 1px var(--stroke-cards) dashed;
    border-radius: 8px;

    @include phone {
      max-width: calc(100vw - 96px);
    }

    &-title {
      font-size: 16px;
      line-height: 22px;
      text-align: center;
    }

    &-description {
      font-size: 14px;
      line-height: 20px;
      margin-top: 4px;
      color: var(--color-text-secondary);
      text-align: center;
    }

    &-button {
      margin-top: 16px;
    }
  }
}
</style>
